import {
  approveCoCTicket,
  approveTicket,
  denyTicket,
  fileTicket,
  onHoldByTicketId,
  recallByTicketId,
  reviseTicket,
  softDeleteTicket,
  submitContractorQuotation,
  submitPurchaseOrder,
  submitPurchaseRequest,
  updateFileTicket,
  updateFileTicketByAc,
  updateFileTicketByMa,
  updateFileTicketByMe,
} from '@/api/maintenance-tickets-api'
import {
  API_FAILURE,
  API_PENDING,
  API_SUCCESS,
  INTERNAL_SERVER_ERROR,
  RESET_INITIAL_STATE,
  UNPROCESSABLE_ENTITY,
} from '@/constants/Actions'

export type MaintenanceTicketsState = {
  loading: boolean
  error: boolean
  errorType: string
  errorMessage: string
  success: boolean
  successMessage: string
}

export const maintenanceTicketsInitialState: MaintenanceTicketsState = {
  loading: true,
  error: false,
  errorType: '',
  errorMessage: '',
  success: false,
  successMessage: '',
}

export type MaintenanceTicketsAction =
  | { type: typeof API_PENDING }
  | { type: typeof API_FAILURE; payload: { type: string; message: string } }
  | { type: typeof API_SUCCESS; payload: { message: string } }
  | { type: typeof RESET_INITIAL_STATE }

export const maintenanceTicketsReducer = (
  state: MaintenanceTicketsState,
  action: MaintenanceTicketsAction,
): MaintenanceTicketsState => {
  const { type } = action

  switch (type) {
    case API_PENDING:
      return {
        ...state,
        loading: true,
        error: false,
        errorType: '',
        errorMessage: '',
        success: false,
        successMessage: '',
      }
    case API_FAILURE:
      if ('payload' in action) {
        return {
          ...state,
          loading: false,
          error: true,
          errorType: action.payload.type,
          errorMessage: action.payload.message,
        }
      }
      return state
    case API_SUCCESS:
      if ('payload' in action) {
        return {
          ...state,
          loading: false,
          success: true,
          successMessage: action.payload.message,
        }
      }
      return state
    case RESET_INITIAL_STATE:
      return {
        ...state,
        loading: false,
        error: false,
        success: false,
        successMessage: '',
        errorMessage: '',
      }
    default:
      return state
  }
}

export const maintenanceTicketsActions = {
  approveMaintenanceTicket:
    (data: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await approveTicket(data)
        if (!response) {
          throw {
            message: 'Failed to approve ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to approve ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Approved ticket successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to approve ticket',
          },
        })
      }
    },
  approveCoCMaintenanceTicket:
    (ticketId: number, data: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await approveCoCTicket(ticketId, data)
        if (!response) {
          throw {
            message: 'Failed to approve ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to approve ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Approved ticket successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to approve ticket',
          },
        })
      }
    },
  denyMaintenanceTicket:
    (data: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await denyTicket(data)
        if (!response) {
          throw {
            message: 'Failed to deny ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to deny ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Deny ticket successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to deny ticket',
          },
        })
      }
    },
  
  softDeleteMaintenanceTicket:
    (data: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await softDeleteTicket(data)
        if (!response) {
          throw {
            message: 'Failed to delete ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to delete ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Delete ticket successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to delete ticket',
          },
        })
      }
    },
  reviseMaintenanceTicket:
    (data: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await reviseTicket(data)
        if (!response) {
          throw {
            message: 'Failed to revise ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to revise ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Revised ticket successfully' },
        })
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to revise ticket',
          },
        })
      }
    },
  updateMaintenanceTicket:
    (
      formDataPayload: FormData,
      ticketId: number,
      dtoParam: any,
      setLoading: any,
      setActiveStep: any,
    ) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        setLoading(true)
        const response = await updateFileTicket(
          formDataPayload,
          ticketId,
          dtoParam,
        )
        if (!response) {
          throw {
            message: 'Failed to update ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to update ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Update ticket successfully' },
        })
        setActiveStep(2)
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to update ticket',
          },
        })
      } finally {
        setLoading(false)
      }
    },
  updateMaintenanceTicketByAc:
    (
      formDataPayload: FormData,
      ticketId: number,
      dtoParam: any,
      setLoading: any,
      setActiveStep: any,
    ) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        setLoading(true)
        const response = await updateFileTicketByAc(
          formDataPayload,
          ticketId,
          dtoParam,
        )
        if (!response) {
          throw {
            message: 'Failed to update ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to update ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Update ticket successfully' },
        })
        setActiveStep(2)
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to update ticket',
          },
        })
      } finally {
        setLoading(false)
      }
    },
  updateMaintenanceTicketByMe:
    (
      formDataPayload: any,
      ticketId: number,
      dtoParam: any,
      setLoading: any,
      setActiveStep: any,
    ) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        setLoading(true)
        const response = await updateFileTicketByMe(
          formDataPayload,
          ticketId,
          dtoParam,
        )
        if (!response) {
          throw {
            message: 'Failed to update ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to update ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Update ticket successfully' },
        })
        setActiveStep(2)
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to update ticket',
          },
        })
      } finally {
        setLoading(false)
      }
    },
  updateMaintenanceTicketByMa:
    (ticketId: number, cost: number, setLoading: any, setActiveStep: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        setLoading(true)
        const response = await updateFileTicketByMa(ticketId, cost)
        if (!response) {
          throw {
            message: 'Failed to update ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to update ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Update ticket successfully' },
        })
        setActiveStep(2)
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to update ticket',
          },
        })
      } finally {
        setLoading(false)
      }
    },
  createMaintenanceTicket:
    (
      formDataPayload: FormData,
      role: string,
      setLoading: any,
      setActiveStep: any,
    ) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        setLoading(true)
        const response = await fileTicket(formDataPayload, role)
        if (!response) {
          throw {
            message: 'Failed to submit ticket. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit ticket.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          const error = {
            message: response.data.message,
            response: {
              status: 422,
              data: response.data
            }
          }
          throw error
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Submit ticket successfully' },
        })

        console.log('finished')
        setActiveStep(2)
      } catch (error) {
        console.log('🚨 Error in createMaintenanceTicket:', error)
        
        // Check if it's an axios error (like 422)
        if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'status' in error.response && error.response.status === 422) {
          console.log('📤 Re-throwing 422 error to FormStepper')
          dispatch({
            type: API_FAILURE,
            payload: {
              type: 'API_ERROR',
              message: (error.response as any)?.data?.message || 'Upload failed',
            },
          })
          throw error
        }
        
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to submit ticket',
          },
        })
        // Re-throw the error so it can be handled by FormStepper
        throw error
      } finally {
        setLoading(false)
      }
    },

  saveContractorQuotation:
    (formDataPayload: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await submitContractorQuotation(formDataPayload)
        if (!response) {
          throw {
            message:
              'Failed to submit Contractor Quotation. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit Contractor Quotation.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Updated Contractor Quotation successfully' },
        })

        console.log('finished')
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to Contractor Quotation',
          },
        })
      } finally {
      }
    },

  savePurchaseRequest:
    (formDataPayload: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await submitPurchaseRequest(formDataPayload)
        if (!response) {
          throw {
            message:
              'Failed to submit Contractor Quotation. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit Contractor Quotation.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Updated Contractor Quotation successfully' },
        })

        console.log('finished')
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to Contractor Quotation',
          },
        })
      } finally {
      }
    },
  savePurchaseOrder:
    (formDataPayload: any) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await submitPurchaseOrder(formDataPayload)
        if (!response) {
          throw {
            message:
              'Failed to submit Contractor Quotation. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit Contractor Quotation.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Updated Contractor Quotation successfully' },
        })

        console.log('finished')
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to Contractor Quotation',
          },
        })
      } finally {
      }
    },
  recallTicket:
    (ticketId: number | string) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await recallByTicketId(ticketId)
        if (!response) {
          throw {
            message:
              'Failed to submit Contractor Quotation. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit Contractor Quotation.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Updated Contractor Quotation successfully' },
        })

        console.log('finished')
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to Contractor Quotation',
          },
        })
      } finally {
      }
    },
  onHoldTicket:
    (ticketId: number | string, note: string) =>
    async (dispatch: (action: MaintenanceTicketsAction) => void) => {
      dispatch({ type: API_PENDING })
      try {
        const response = await onHoldByTicketId(ticketId, note)
        if (!response) {
          throw {
            message:
              'Failed to submit Contractor Quotation. Please check your network.',
            type: 'API/Network failure',
          }
        } else if (response.data.status === INTERNAL_SERVER_ERROR) {
          throw {
            message: 'Failed to submit Contractor Quotation.',
            type: response.data.message,
          }
        } else if (response.data.httpStatus === UNPROCESSABLE_ENTITY) {
          throw {
            message: response.data.message,
          }
        }
        dispatch({
          type: API_SUCCESS,
          payload: { message: 'Updated Contractor Quotation successfully' },
        })

        console.log('finished')
      } catch (error) {
        dispatch({
          type: API_FAILURE,
          payload: {
            type: 'API_ERROR',
            message: 'Failed to Contractor Quotation',
          },
        })
      } finally {
      }
    },
}
